import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { QueryUtils } from '../utils/queryUtils';

/**
 * Hook to manage React Query cache based on user authentication state
 * Automatically clears cache when user changes or logs out
 */
export const useAuthCacheManager = () => {
  const queryClient = useQueryClient();
  const { me, logged } = useSelector((store: any) => store.auth);
  const previousUserIdRef = useRef<string | number | null>(null);
  const previousLoggedStateRef = useRef<boolean | null>(null);

  useEffect(() => {
    const currentUserId = me?.id;
    const previousUserId = previousUserIdRef.current;
    const previousLoggedState = previousLoggedStateRef.current;

    // If user logged out
    if (previousLoggedState === true && logged === false) {
      console.log('User logged out, clearing React Query cache');
      QueryUtils.clearAllCache(queryClient);
    }
    
    // If user changed (different user logged in)
    else if (
      logged && 
      currentUserId && 
      previousUserId && 
      currentUserId !== previousUserId
    ) {
      console.log('Different user logged in, clearing React Query cache');
      QueryUtils.clearAllCache(queryClient);
    }

    // Update refs
    previousUserIdRef.current = currentUserId;
    previousLoggedStateRef.current = logged;
  }, [me?.id, logged, queryClient]);

  // Return utility functions for manual cache management
  return {
    clearCache: () => QueryUtils.clearAllCache(queryClient),
    clearUserSpecificCache: (userId?: string | number) => 
      QueryUtils.clearUserSpecificCache(queryClient, userId),
    resetCacheForLogout: () => QueryUtils.resetCacheForLogout(queryClient),
  };
};
